import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function SmtpTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get('/smtp/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const update = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { setting_id, ...data } = settings;
      await api.put('/smtp/settings', data);
      toast.success('Configuration SMTP sauvegardee');
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await api.post('/smtp/test');
      toast.success('Email de test envoye');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Echec du test SMTP');
    } finally {
      setTesting(false);
    }
  };

  if (!settings) return <div className="text-center py-8 text-muted-foreground">Chargement...</div>;

  return (
    <div className="space-y-4">
      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Configuration SMTP</CardTitle>
          <CardDescription>Configurez le serveur de messagerie pour les notifications par email</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Serveur SMTP</Label>
              <Input value={settings.smtp_host || ''} onChange={e => update('smtp_host', e.target.value)} placeholder="smtp.gmail.com" data-testid="smtp-host" />
            </div>
            <div className="space-y-2">
              <Label>Port</Label>
              <Input type="number" value={settings.smtp_port || 587} onChange={e => update('smtp_port', parseInt(e.target.value) || 587)} data-testid="smtp-port" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Utilisateur</Label>
              <Input value={settings.smtp_user || ''} onChange={e => update('smtp_user', e.target.value)} data-testid="smtp-user" />
            </div>
            <div className="space-y-2">
              <Label>Mot de passe</Label>
              <Input type="password" value={settings.smtp_password || ''} onChange={e => update('smtp_password', e.target.value)} data-testid="smtp-password" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email expediteur</Label>
              <Input value={settings.smtp_from_email || ''} onChange={e => update('smtp_from_email', e.target.value)} placeholder="noreply@example.com" data-testid="smtp-from-email" />
            </div>
            <div className="space-y-2">
              <Label>Nom expediteur</Label>
              <Input value={settings.smtp_from_name || ''} onChange={e => update('smtp_from_name', e.target.value)} placeholder="Carte Diaspora RDC" data-testid="smtp-from-name" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={settings.smtp_use_tls !== false} onCheckedChange={v => update('smtp_use_tls', v)} data-testid="smtp-tls-switch" />
            <Label>Utiliser TLS</Label>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleSave} disabled={saving} data-testid="save-smtp-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
        <Button variant="outline" onClick={handleTest} disabled={testing || !settings.smtp_host} data-testid="test-smtp-btn">
          {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Tester
        </Button>
      </div>
    </div>
  );
}
